import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { ScoreUploaderState } from "../../../../state/admin/score-uploader/ScoreUploaderState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUploadAlt,
  faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { Badge, CheckBox } from "../../../../components/common";
import scoreUploadApi from "../../../../api/dashboard/scoreUploadApi";
import { useAuth } from "react-oidc-context";
import ValidationResult from "../../../../types/forms/ValidationResult";
import ValidationWarning from "../../../../components/common/ValidationWarning";
import { ScoreUploaderStep } from "../../../../types/admin/score-uploader/ScoreUploaderStep";
import { InitialiseScoreUploadResponse } from "../../../../types/upload/InitialiseScoreUploadResponse";
import DateInput from "../../../../components/common/DateInput";
import Label from "../../../../components/common/Label";

interface ScoreUploaderFileSelectionProps {
  state: ScoreUploaderState;
  onStateChange: (
    state: ScoreUploaderState | Partial<ScoreUploaderState>
  ) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = {
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/vnd.ms-excel": [".xls"],
};

const ScoreUploaderFileSelection: React.FC<ScoreUploaderFileSelectionProps> = ({
  state,
  onStateChange,
}) => {
  const auth = useAuth();
  const scoreApi = new scoreUploadApi(auth.user?.access_token);

  const validateFileClientSide = (file: File | undefined): ValidationResult => {
    if (!file) {
      return new ValidationResult(false, [
        {
          errorType: "CUSTOM",
          customMessage: "Please select a file to upload",
        },
      ]);
    }

    // Check file type
    const fileExtension = file.name.toLowerCase().split(".").pop();
    const isValidType =
      fileExtension === "csv" ||
      fileExtension === "xlsx" ||
      fileExtension === "xls";
    if (!isValidType) {
      return new ValidationResult(false, [
        {
          errorType: "CUSTOM",
          customMessage: "Please upload a CSV or Excel file",
        },
      ]);
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return new ValidationResult(false, [
        {
          errorType: "CUSTOM",
          customMessage: "File size must be less than 10MB",
        },
      ]);
    }

    return new ValidationResult(true);
  };

  const validateDateField = (): ValidationResult => {
    if (!state.dataDate) {
      return new ValidationResult(false, [
        {
          errorType: "REQUIRED",
          customMessage: "Please select a date for the scores",
        },
      ]);
    }
    return new ValidationResult(true);
  };

  const initialiseUpload = useCallback(
    (file: File) => {
      onStateChange({
        ...state,
        uploadedFile: file,
        isLoading: true,
        validationResult: null,
        showValidationErrors: false,
      });

      scoreApi.initialiseExternalScoreUpload(
        file,
        state.hasHeaderRow,
        state.dataDateString,
        (response: InitialiseScoreUploadResponse) => {
          onStateChange({
            ...state,
            uploadedFile: file,
            isLoading: false,
            totalRows: response.totalRows,
            suggestedMappings: response.suggestedMappings,
            availableMappings: response.availableMappingOptions,
            uploadId: response.uploadId,
            identifierColumn: "",
            identifierType: undefined,
            scoreMappings: [],
            validationResult: null,
            showValidationErrors: false,
            currentStep: ScoreUploaderStep.MapColumns,
            hasSuggestedMappingsBeenInitialized: false,
          });
        },
        (error: any) => {
          onStateChange({
            ...state,
            uploadedFile: file,
            isLoading: false,
            validationResult: new ValidationResult(false, [
              {
                errorType: "CUSTOM",
                customMessage: "Failed to process file. Please try again.",
              },
            ]),
            showValidationErrors: true,
          });
        }
      );
    },
    [state, onStateChange, scoreApi]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        onStateChange({
          ...state,
          uploadedFile: file,
          validationResult: null,
          showValidationErrors: false,
        });
      }
    },
    [state, onStateChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  const handleHeaderRowChange = (isChecked: boolean) => {
    onStateChange({
      ...state,
      hasHeaderRow: isChecked,
    });
  };

  const handleDateChange = (newDate: Date | null) => {
    onStateChange({
      ...state,
      dataDate: newDate || new Date(),
    });
  };

  const handleNext = () => {
    const fileValidation = validateFileClientSide(state.uploadedFile);
    const dateValidation = validateDateField();

    // Combine validation results
    const isValid = fileValidation.isValid && dateValidation.isValid;
    const errors = [
      ...(fileValidation.errors || []),
      ...(dateValidation.errors || []),
    ];

    const combinedValidation = new ValidationResult(isValid, errors);

    onStateChange({
      ...state,
      validationResult: combinedValidation,
      showValidationErrors: true,
    });

    if (isValid) {
      initialiseUpload(state.uploadedFile!);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg flex items-start text-sm">
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="text-blue-500 mt-1 mr-3"
        />
        <p className="text-blue-700">
          Upload a CSV or Excel file containing employee identifiers and their
          corresponding scores. You'll also set a date that these scores apply
          to. The system will update records using this date and the file
          contents.
        </p>
      </div>

      <div className="bg-white rounded-lg">
        <div className="mb-4 border border-gray-200 rounded-lg p-6 space-y-4">
          <div>
            <div className="mb-2">
              <Label
                text="Set the date to be applied to the scores"
                htmlFor="score-date-input"
                className="text-sm font-medium text-gray-700"
              />
              <Badge
                text="Required"
                textColourClassName="text-gray-600"
                marginClassName="ml-2"
              />
            </div>
            <DateInput
              value={state.dataDate}
              onChange={handleDateChange}
              inputId="score-date-input"
              placeholder="Select the date for these scores"
              allowDatesInPast={true}
              allowDatesInFuture={true}
              isClearable={false}
              showValidationErrors={state.showValidationErrors}
              validationResult={
                state.showValidationErrors && !state.dataDate
                  ? new ValidationResult(false, [
                      {
                        errorType: "REQUIRED",
                        customMessage: "Please select a date for the scores",
                      },
                    ])
                  : null
              }
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <CheckBox
            selected={state.hasHeaderRow}
            onChange={handleHeaderRowChange}
            labelText="Does this file contain a header row?"
            containerClassNames="flex items-center"
            checkboxClassNames="h-5 w-5 border border-gray-300 rounded focus:ring-1 focus:ring-offset-0 focus:ring-gray-400"
            labelClassNames="text-sm font-medium text-gray-700"
            controlId="scoreUploaderHeaderRowCheckbox"
          />
        </div>

        {state.showValidationErrors && state.validationResult && (
          <div className="mb-4">
            <ValidationWarning
              isValid={state.validationResult.isValid}
              errors={state.validationResult.errors}
            />
          </div>
        )}

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? "border-blue-400 bg-blue-50"
              : "border-gray-300 hover:border-gray-400"
          } ${state.uploadedFile ? "bg-gray-50" : ""}`}
        >
          <input {...getInputProps()} />
          {state.uploadedFile ? (
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-900">
                {state.uploadedFile.name}
              </p>
              <p className="text-sm text-gray-500">
                {(state.uploadedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                className="h-12 w-12 text-gray-400"
              />
              <div>
                <p className="text-lg font-medium text-gray-900">
                  Drag and drop your .csv or .xlsx file here
                </p>
                <p className="text-sm text-gray-500 mt-1">or</p>
                <button className="mt-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium text-gray-700">
                  Browse Files
                </button>
              </div>
              <p className="text-sm text-gray-500">Maximum file size: 10MB</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          className="px-6 py-2 btn-primary rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleNext}
          disabled={state.isLoading}
        >
          {state.isLoading ? "Processing..." : "Next"}
        </button>
      </div>
    </div>
  );
};

export default ScoreUploaderFileSelection;
